import React from 'react'
import * as echarts from 'echarts'
import { Logger } from 'wdc-cube'
import { makeStyles } from 'tss-react/mui'
import { classToFComponent, LifesHubColors } from 'src/utils/views'
import { EChartViewClass } from './helpers/base-echart'

import { ChartFontSize, ChartPalette, axisLabelStyle, axisLineStyle, splitLineStyle } from './helpers/tcm_chart_theme'
import { formatNumberToCompactShortForm } from './helpers/tcm_view_utils'
import { ChartEmpresasAbertasFechadasScope, type DataZoom } from '../tcm_scopes'
import { JSX } from 'react/jsx-runtime'
import { TcmCard } from 'src/utils/views/modules/the-consumer-market'

const LOG = Logger.get('TCM-EVOLUCAO-ABERTA-FECHADAS')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'hidden'
    }
})

type ChartAbertasFechadasProps = {
    className?: string
    style?: React.CSSProperties
    scope: ChartEmpresasAbertasFechadasScope
}

class ChartAbertasFechadasViewClass extends EChartViewClass<ChartAbertasFechadasProps> {
    // :: Fields
    scope!: ChartEmpresasAbertasFechadasScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartAbertasFechadasProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = this.scope.loaded
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartAbertasFechadasProps): React.JSX.Element {
        return (
            <TcmCard caption={scope.caption} className={className} style={style}>
                {super.render({ className: this.classes.view, scope })}
            </TcmCard>
        )
    }

    override onBindChartEvents(chart: echarts.ECharts): void {
        //highlight
        chart.off('dataZoom', this.emitOnDataZoom)
        chart.on('dataZoom', this.emitOnDataZoom)

        chart.off('restore', this.emitRestore)
        chart.on('restore', this.emitRestore)
    }

    emitOnDataZoom = (event: unknown) => {
        const evt = event as {
            batch: DataZoom[]
        }

        if (evt.batch && evt.batch.length > 0) {
            const src = evt.batch[0]
            this.scope
                .onDataZoom({
                    startValue: src.startValue!,
                    endValue: src.endValue!
                })
                .catch(LOG.caught)
        }
    }

    emitRestore = () => {
        this.scope.onRestore().catch(LOG.caught)
    }
}

export default classToFComponent(ChartAbertasFechadasViewClass, React)

type CallbackDataParams = Parameters<echarts.LabelFormatterCallback>[0]

const SERIE_ABERTAS = 'Empresas abertas por mês'
const SERIE_FECHADAS = 'Empresas fechadas por mês'
const SERIE_SALDO = 'Saldo de empresas por mês'

const tooltipRow = (marker: string, label: string, value: number) => `
  <div style="display:flex;align-items:center;gap:8px;margin-top:7px;line-height:1.2">
      ${marker}
      <span style="color:${LifesHubColors.muted};font-size:12px">${label}</span>
      <span style="flex:1"></span>
      <span style="color:${LifesHubColors.ink};font-size:12px;font-weight:800">${formatNumberToCompactShortForm(
          value
      )}</span>
  </div>`

const tooltipTemplate = (params: CallbackDataParams[]) => `
  <div style="min-width:230px">
      <div style="color:${LifesHubColors.ink};font-size:12px;font-weight:700;line-height:1.2">${params[0].name}</div>
      ${tooltipRow(params[0].marker as string, params[0].seriesName as string, params[0].value as number)}
      ${tooltipRow(params[1].marker as string, params[1].seriesName as string, params[1].value as number)}
      ${tooltipRow(params[2].marker as string, SERIE_SALDO, params[2].value as number)}
  </div>`

const computePoints = (x1: number, y1: number, x2: number, y2: number, steps: number) => {
    const a = y2 - y1
    const b = x2 - x1

    const yStep = a / steps
    const xStep = b / steps

    const result: [number, number][] = []
    for (let i = 0; i < steps; i++) {
        const xCur = Math.round(x1 + xStep * i)
        const yCur = Math.round(y1 + yStep * i)
        result.push([xCur, yCur])
    }
    return result
}

function buildChartData(scope: ChartEmpresasAbertasFechadasScope): echarts.EChartsOption {
    const xAxisLabelData = scope.dataset.map((r) => r[0])
    const abertaValueData = scope.dataset.map((r) => r[1])
    const fechadaValueData = scope.dataset.map((r) => r[2])
    const diferencaValueData = scope.dataset.map((r) => r[1] - r[2])

    // prettier-ignore
    const trendValueData = (function () {
        const valueData = diferencaValueData
        const x1 = 0
        const x2 = Math.max(valueData.length - 2, x1)
        const y1 = (valueData[x1] - valueData[x1])/2 + Math.min(valueData[x1], valueData[x1])
        const y2 = (valueData[x2] - valueData[x2])/2 + Math.min(valueData[x2], valueData[x2])
        return computePoints(x1, y1, x2, y2, valueData.length).map(v => v[1])
    })()

    return {
        tooltip: {
            trigger: 'axis',
            confine: false,
            appendToBody: true,
            axisPointer: {
                type: 'shadow'
            },
            backgroundColor: '#ffffff',
            borderColor: LifesHubColors.border,
            borderWidth: 1,
            padding: [10, 12],
            extraCssText: 'border-radius: 10px; box-shadow: 0 20px 70px rgba(0, 0, 0, .24);',
            formatter: (params) => {
                return tooltipTemplate(params as CallbackDataParams[])
            }
        },
        toolbox: {
            left: 'center',
            itemSize: 18,
            top: 30,
            iconStyle: { borderColor: ChartPalette.label },
            emphasis: { iconStyle: { borderColor: LifesHubColors.green800 } },
            feature: {
                dataZoom: {
                    yAxisIndex: 'none'
                },
                restore: {}
            }
        },
        color: ChartPalette.multi,
        legend: {
            icon: 'circle',
            itemHeight: 8,
            itemWidth: 8,
            textStyle: { color: ChartPalette.label, fontSize: ChartFontSize.axis },
            data: [{ name: SERIE_ABERTAS }, { name: SERIE_FECHADAS }]
        },
        grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            containLabel: true
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: xAxisLabelData,
            axisTick: { show: false },
            axisLine: axisLineStyle,
            axisLabel: axisLabelStyle
        },
        yAxis: {
            type: 'value',
            splitLine: splitLineStyle,
            axisLabel: {
                ...axisLabelStyle,
                formatter: formatNumberToCompactShortForm
            }
        },
        series: [
            {
                name: SERIE_ABERTAS,
                type: 'line',
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: { width: 2 },
                data: abertaValueData
            },
            {
                name: SERIE_FECHADAS,
                type: 'line',
                symbol: 'circle',
                symbolSize: 6,
                lineStyle: { width: 2 },
                data: fechadaValueData
            },
            {
                name: SERIE_SALDO,
                type: 'line',
                symbol: 'none',
                lineStyle: { width: 2 },
                data: diferencaValueData
            },
            {
                type: 'line',
                symbol: 'none',
                data: trendValueData,
                lineStyle: {
                    type: 'dashed',
                    width: 1.5
                }
            }
        ]
    }
}
